import { Component, useEffect, useLayoutEffect, useState, useRef, useCallback, type ComponentType, ErrorInfo, ReactNode } from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation, useNavigationType, useParams } from 'react-router-dom';
import { trackException } from './telemetry';
import { logBackgroundError } from './observability';
import { finishPendingSignOut, getMe, getVersion } from './api/client';
import { useAuthStore } from './store/auth';
import { signOutStamp } from './lib/signOuts';
import { useI18n } from './i18n';
import { entryKey, persistPositions, recordPosition, restorePosition, scrollAtOnce, setEntryOnScreen, storedPosition } from './lib/scrollRestore';

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    trackException(error, { componentStack: info.componentStack || '' });
  }
  render() {
    if (this.state.hasError) {
      // A class component cannot call the hook, so it reads the store (#458).
      const { t } = useI18n.getState();
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2">{t.common.error}</h1>
            <p className="text-gray-400 mb-4">{t.app.crashText}</p>
            <button onClick={() => window.location.reload()}
              className="bg-blue-600 px-4 py-2 rounded-lg hover:bg-blue-700">{t.app.reload}</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/** #595 — one page for each item: a notice link, Back or an opponent link
 *  moves an open detail page to another item, and the state of the old item
 *  stayed. An open edit form of a team saved its values into the other team,
 *  and a late failed load showed "Team not found". */
function ById({ page: Page }: { page: ComponentType }) {
  const { id } = useParams<{ id: string }>();
  return <Page key={id} />;
}

/** #661 (F53) — a route that needs a signed-in user. A guest goes to the sign-in
 *  page with `replace`, so the guarded URL leaves the history: Back leaves the
 *  sign-in page. The pages pushed `/login` from an effect, and Back returned to
 *  the guarded page, which pushed `/login` again: Back never left (bug 65).
 *  The wrapper reads the store, so it also reacts when the session ends. */
function RequireUser({ children }: { children: ReactNode }) {
  const signedIn = useAuthStore(s => !!s.user);
  return signedIn ? <>{children}</> : <Navigate to="/login" replace />;
}

/** #661 (F53) — a route for some roles. A guest goes to the sign-in page, a
 *  signed-in user without the role goes home, both with `replace`. */
function RequireRole({ roles, children }: { roles: string[]; children: ReactNode }) {
  const signedIn = useAuthStore(s => !!s.user);
  const allowed = useAuthStore(s => s.hasRole(...roles));
  if (!signedIn) return <Navigate to="/login" replace />;
  return allowed ? <>{children}</> : <Navigate to="/" replace />;
}

function ScrollToTop() {
  const { pathname, search, hash, key } = useLocation();
  const navType = useNavigationType();

  // #595 — the app restores the position on Back, not the browser: the
  // browser runs before the rows of a list exist (lib/scrollRestore.ts).
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.addEventListener('scroll', recordPosition, { passive: true });
    window.addEventListener('pagehide', persistPositions);
    return () => {
      window.removeEventListener('scroll', recordPosition);
      window.removeEventListener('pagehide', persistPositions);
    };
  }, []);

  // A layout effect: it runs when React commits the new page, before the
  // browser paints it and before a scroll event of it. So a position goes
  // under the entry of its own page, and a new page never shows at the
  // position of the old one.
  const shownPath = useRef<string | null>(null);
  useLayoutEffect(() => {
    const samePage = shownPath.current === pathname;
    shownPath.current = pathname;
    const entry = entryKey(key, pathname, search);
    setEntryOnScreen(entry);
    persistPositions();
    // Back, Forward and a reload: the stored position of this history entry,
    // else the top. An entry that was never scrolled has no position.
    if (navType === 'POP') return restorePosition(storedPosition(entry) ?? 0);
    // A change of the URL in place (a tab, a filter): the page stays where it
    // is, and the new entry takes its position.
    if (navType === 'REPLACE' && samePage) {
      recordPosition();
      return;
    }
    // A new page (also a REPLACE to another page, for example after a
    // delete): the hash anchor, or the top.
    if (hash) {
      const targetId = hash.slice(1);
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        if (attempts++ < 30) requestAnimationFrame(tryScroll); // retry ~500ms
      };
      requestAnimationFrame(tryScroll);
    } else {
      scrollAtOnce(0);
    }
  }, [pathname, search, hash, key, navType]);

  return null;
}
// VersionCheck reads the version through getVersion() (axios) so it inherits
// the shared retry/timeout/telemetry pipeline in api/client.ts. In-arena
// WiFi flakes that previously surfaced as `AxiosError: Network Error` in
// the Exceptions blade now silently retry, and exhausted retries downgrade
// to a customEvent via logBackgroundError's background-poll classification.
function VersionCheck() {
  const { t } = useI18n();
  // #604, #608 — the ring screens are silent: no banner over the video of a
  // stream (the overlay), and none on a TV that nobody attends (the display).
  const onRingScreen = /^\/ring-(overlay|display)\//.test(useLocation().pathname);
  const [showUpdate, setShowUpdate] = useState(false);
  const knownVersion = useRef<string | null>(null);

  const check = useCallback(async () => {
    // Skip outright when the OS-level network is down. navigator.onLine is
    // notoriously unreliable for "WiFi associated but no internet" cases
    // (which is most in-arena flake) — but it's correct for "airplane mode
    // / no network adapter" cases. Skipping on those avoids generating a
    // guaranteed-fail dependency entry in App Insights.
    if (!navigator.onLine) return;
    getVersion()
      .then(res => {
        const { version } = res.data;
        if (!knownVersion.current) {
          knownVersion.current = version;
        } else if (version !== knownVersion.current) {
          setShowUpdate(true);
        }
      })
      .catch(logBackgroundError('App:version-check'));
  }, []);

  useEffect(() => {
    check();
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    const onOnline = () => check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [check]);

  if (!showUpdate || onRingScreen) return null;
  return (
    <div className="fixed top-0 inset-x-0 z-100 bg-blue-600 text-white text-center py-2.5 px-4 text-sm font-medium shadow-lg flex items-center justify-center gap-3">
      <span>{t.app.newVersion}</span>
      <button onClick={() => window.location.reload()}
        className="bg-white text-blue-600 font-bold px-3 py-1 rounded-lg text-xs hover:bg-blue-50">
        {t.app.update}
      </button>
    </div>
  );
}

import Layout from './components/Layout';
import { ToastProvider } from './hooks/useToast';
import { ConfirmProvider } from './hooks/useConfirm';
import Home from './pages/Home';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import TeamList from './pages/TeamList';
import TeamDetail from './pages/TeamDetail';
import TournamentList from './pages/TournamentList';
import TournamentDetail from './pages/TournamentDetail';
import BoutDetail from './pages/BoutDetail';
import Manual from './pages/Manual';
import RingDisplay from './pages/RingDisplay';
import RingOverlay from './pages/RingOverlay';
import Athletes from './pages/Athletes';
import AthleteDetail from './pages/AthleteDetail';
import Officials from './pages/Officials';
import OfficialDetail from './pages/OfficialDetail';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import NotFoundState from './components/shared/NotFoundState';
import { QuestionMarkIcon } from './components/shared/Icons';

// Routes inside the normal Layout (header/nav/footer)
function LayoutRoutes() {
  const { t } = useI18n();
  return (
    <Layout>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/profile" element={<RequireUser><Profile /></RequireUser>} />
        <Route path="/admin" element={<RequireRole roles={['admin', 'team_manager']}><Admin /></RequireRole>} />
        {/* #595 — a list page and a detail page. The notices link to /teams/<id>. */}
        <Route path="/teams" element={<TeamList />} />
        <Route path="/teams/:id" element={<ById page={TeamDetail} />} />
        <Route path="/tournaments" element={<TournamentList />} />
        <Route path="/tournaments/:id" element={<ById page={TournamentDetail} />} />
        <Route path="/bouts/:id" element={<BoutDetail />} />
        <Route path="/manual" element={<Manual />} />
        <Route path="/athletes" element={<Athletes />} />
        <Route path="/athletes/:id" element={<ById page={AthleteDetail} />} />
        {/* #616 — the statistics of the officials. */}
        <Route path="/officials" element={<RequireUser><Officials /></RequireUser>} />
        <Route path="/officials/:id" element={<RequireUser><ById page={OfficialDetail} /></RequireUser>} />
        <Route path="*" element={<NotFoundState icon={<QuestionMarkIcon className="w-12 h-12" />} title={t.app.pageNotFound} to="/" linkLabel={t.nav.home} />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  // Phase 2 boot — Zustand `user` blob is optimistic hydration from
  // localStorage; the cookie session is the source of truth. If we have a
  // user blob but no valid cookie (expired / signed out elsewhere), getMe()
  // 401s and the api/client 401 interceptor calls clearAuth + redirects.
  // No user blob = anonymous boot; skip.
  // #666 — The answer replaces the stored user, also with another id. A
  // sign-out after the stamp makes the answer old, and the store ignores it.
  // A sign-out that did not reach the server goes first: until its POST gets
  // an answer, the browser can hold the cookies of that session. The store
  // then starts with no user, so the read of the user does not run. A failure
  // of the sign-out goes to the telemetry, and the read of the user still runs.
  useEffect(() => {
    void finishPendingSignOut()
      .catch(logBackgroundError('boot:signout'))
      .then(() => {
        if (!useAuthStore.getState().user) return;
        const stamp = signOutStamp();
        getMe()
          .then(res => useAuthStore.getState().refreshAuth(res.data, stamp))
          .catch(() => { /* 401 path handled by api/client interceptor */ });
      });
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <VersionCheck />
        <Routes>
          {/* Fullscreen TV display — intentionally OUTSIDE <ToastProvider>.
              RingDisplay is an unattended TV surface; no user to dismiss a
              popup. Errors go to telemetry (logBackgroundError /
              trackException), not pixels. useToast() throws in this
              subtree — that's the intentional guard against future
              accidental imports. See agent-docs/code-patterns.md § Toast
              kinds for the full rule. */}
          <Route path="/ring-display/:tournamentId/:ringId" element={<RingDisplay />} />
          {/* #604 — the broadcast overlay: the same rule, and no version banner. */}
          <Route path="/ring-overlay/:tournamentId/:ringId" element={<RingOverlay />} />
          {/* Everything else gets the standard Layout, the toast host and the
              question host (#665, useConfirm). The ring screens above have
              neither: nobody answers a question on a TV. */}
          <Route path="*" element={<ToastProvider><ConfirmProvider><LayoutRoutes /></ConfirmProvider></ToastProvider>} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
